import React, { useState, useEffect } from 'react';
import { Input, message, Tooltip, Dropdown, Menu, Spin } from 'antd';
import { FaInfoCircle } from 'react-icons/fa';
import { getRemoteVideoDurationSec, getYoutubeDurationSec } from '../../services/localVideoDuration';
// import { MdPlayCircleFilled } from "react-icons/md";

import fileEdit from '../../Assets/schedule/fileEdit.svg';
import question from '../../Assets/schedule/question.svg';
// import downloadIcon from '../../Assets/schedule/download.svg';
// import publishIcon from '../../Assets/schedule/publish.svg';
// import unpublishIcon from '../../Assets/schedule/unpublish.svg';
// import deleteIcon from '../../Assets/schedule/delete.svg';
import threeDotsVertical from '../../Assets/schedule/three-dots-vertical.svg';
import emptyImage from '../../Assets/schedule/Group 10647.svg';
import videoThumbnailPlaceholder from '../../Assets/schedule/Rectangle 137038.png';
import { Api } from '../../services';
import SavingModal from '../../components/SavingModal';

/* ================= RESOURCE ITEM =================
   Defined at module scope (not inside ResourcesPanel) so its identity stays
   stable across ResourcesPanel re-renders. Defining a component inline inside
   another component's render body causes React to remount it (and its Popover's
   open state) on every parent re-render — that was making the "..." menu appear
   to do nothing on click. */
const ResourceItem = (props) => {
  const {
    id,
    type,
    title,
    size,
    videoDuration,
    documentPages,
    questions,
    thumbnail,
    filePath,
    file,
    link,
    showPublish = true,
    showDownload = true,
    showEdit = true,
    isReadOnly = false,
    onEdit,
    onRename,
    onDownload,
    onView,
    onPublish,
    onUnpublish,
    onDelete,
    onPlay,
    requestConfirm,
    published,
    transcoded = true,
    isVirtual = false,
    roomName
  } = props;
  const [isPublished, setIsPublished] = useState(!!published);
  // `useState(!!published)` only reads the prop on this card's very first mount —
  // it never re-runs on its own, so once a fresh `published` value flows back down
  // (a refetch after this same publish/unpublish action, or picking up a change made
  // from another device/session — the whole point of asking the server at all) the
  // label was stuck showing whatever it happened to be at mount, ignoring the server's
  // actual current value. Keep it in sync with the prop on every change instead.
  useEffect(() => {
    setIsPublished(!!published);
  }, [published]);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [computedDuration, setComputedDuration] = useState(null);

  // A YouTube resource is a link, not a file of ours: there are no bytes to hand over,
  // and the download flow would go looking for an S3 key that was never written. Kept to
  // YouTube specifically rather than "has a link at all", because a locally uploaded
  // video can also carry `link` as an absolute URL to its own file - that one is
  // downloadable (see ScheduleDetailsDrawer's handlePlayResource for the same
  // distinction).
  const isYoutubeLink = Boolean(link && (link.includes('youtube.com') || link.includes('youtu.be')));

  // library-contents doesn't always echo back the videoDuration we computed
  // client-side at upload time (see ScheduleDetailsDrawer.js's categorizedResources
  // comment) — when that happens the card is left with no duration at all. Derive
  // it ourselves straight from the file, the same way ResourceAddForm.js does for
  // the upload preview, using the same S3 URL resolution handlePlayResource uses.
  useEffect(() => {
    const hasDuration = videoDuration && videoDuration !== 'N/A' && videoDuration !== 0;
    if (type !== 'video' || hasDuration) {
      setComputedDuration(null);
      return;
    }
    // A YouTube resource has no file behind it, so it needs its own lookup - the player
    // API answers it (see getYoutubeDurationSec). It used to be excluded outright here,
    // which is why those cards never showed a length.
    if (isYoutubeLink) {
      let cancelledYt = false;
      getYoutubeDurationSec(link).then((seconds) => {
        if (!cancelledYt && seconds > 0) setComputedDuration(Math.round(seconds));
      });
      return () => { cancelledYt = true; };
    }

    const rawPath = filePath || file || props.videoPath || props.path || props.s3Path || props.fileKey || props.key;
    let url = null;
    if (rawPath) {
      url = /^https?:\/\//i.test(rawPath) ? rawPath : `${Api.dlS3Url}${rawPath}`;
    } else if (link) {
      url = link;
    }
    if (!url) return;

    // Was a bare <video> element, which the browser cannot read for .flv or .avi - so
    // those cards fell through both routes and showed no duration at all.
    // getRemoteVideoDurationSec keeps <video> for what the browser decodes and reads the
    // duration out of a ranged header fetch for those two.
    let cancelled = false;
    getRemoteVideoDurationSec(url).then((seconds) => {
      if (cancelled || !(seconds > 0)) return;
      // Keep this in seconds, not pre-floored to whole minutes (with a forced
      // minimum of 1) - that used to make every sub-minute video display as
      // "1 min" instead of its actual length. formatDuration below is what
      // decides whether to show it as seconds or minutes.
      setComputedDuration(Math.round(seconds));
    });
    return () => { cancelled = true; };
  }, [type, videoDuration, filePath, file, link, isYoutubeLink]);

  // videoDuration (from the server) is already in whole minutes; computedDuration
  // (our own client-side fallback above) is in seconds, so it can't be swapped in
  // interchangeably the way effectiveVideoDuration used to - each needs its own
  // formatting below.
  const formatDuration = (totalSeconds) => {
    const seconds = Math.max(0, Math.round(totalSeconds));
    if (seconds < 60) {
      return `${Math.max(1, seconds)} sec`;
    }
    const mins = Math.floor(seconds / 60);
    const remainderSecs = seconds % 60;
    if (remainderSecs === 0) {
      return mins === 1 ? '1 min' : `${mins} mins`;
    }
    // Keep the leftover seconds instead of rounding them away - a 4 min 20 sec
    // video should read "4 mins 20 secs", not get rounded up/down to a bare "4 mins".
    const minsLabel = mins === 1 ? '1 min' : `${mins} mins`;
    const secsLabel = remainderSecs === 1 ? '1 sec' : `${remainderSecs} secs`;
    return `${minsLabel} ${secsLabel}`;
  };

  const handleRenameSubmit = () => {
    if (!editValue || editValue.trim() === '') {
      message.error('Title cannot be empty');
      setEditValue(title);
      setIsEditing(false);
      return;
    }
    if (editValue.trim() !== title && onRename) {
      onRename(id, editValue.trim());
    }
    setIsEditing(false);
  };

  const getIcon = () => {
    if (type === 'questionnaire') return question;
    return fileEdit;
  };

  const getMetadata = () => {
    // 1. Size calculation (size is in Bytes)
    // No size at all means we don't know it, not that the file is empty - a "0 KB"
    // label on a recording that plays for a minute and downloads as 102 KB is just
    // wrong. Leave the size out of the metadata line entirely in that case
    // (formattedSize stays null). Restore this to label an unknown size as empty:
    // let formattedSize = '0 KB';
    let formattedSize = null;
    if (size && !isNaN(size)) {
      const bytes = parseFloat(size);
      // For video recordings, any size under 10 KB (e.g. ~500 bytes) is an error/playlist body,
      // not a valid video file. Ignore it so it never shows "0.49 KB".
      const isVideoResource = isVideoType || resourceType === 'recording' || resourceType === 'video';
      if (!isVideoResource || bytes >= 10240) {
        const mb = bytes / (1024 * 1024);
        if (mb < 1) {
          // Below 1 MB, show size in KB instead of "0.00 MB"
          const kb = bytes / 1024;
          formattedSize = `${(kb < 1 ? kb.toFixed(2) : kb.toFixed(1))} KB`;
        } else {
          // Show 1 decimal place if it's not a whole number, else whole number
          formattedSize = `${mb.toFixed(1)} MB`;
        }
      }
    } else if (size) {
      formattedSize = size.toString().toLowerCase().includes('mb') ? size : `${size} MB`;
    }

    const parts = formattedSize ? [formattedSize] : [];

    // 2. Metadata (videoDuration/documentPages/questions)
    // Duration for video
    const hasServerDuration = videoDuration && videoDuration !== 'N/A' && videoDuration !== 0;
    if (hasServerDuration) {
      if (typeof videoDuration === 'string' && videoDuration.includes(':')) {
        const partsHms = videoDuration.split(':').map(Number);
        let sec = 0;
        if (partsHms.length === 3) {
          sec = partsHms[0] * 3600 + partsHms[1] * 60 + partsHms[2];
        } else if (partsHms.length === 2) {
          sec = partsHms[0] * 60 + partsHms[1];
        }
        parts.push(formatDuration(sec));
      } else if (videoDuration.toString().toLowerCase().includes('min') || videoDuration.toString().toLowerCase().includes('sec')) {
        parts.push(videoDuration);
      } else {
        const num = Number(videoDuration);
        if (!isNaN(num) && num > 0) {
          if (num > 10000) {
            parts.push(formatDuration(Math.round(num / 1000)));
          } else {
            parts.push(formatDuration(Math.round(num * 60)));
          }
        }
      }
    } else if (computedDuration !== null && computedDuration !== undefined) {
      parts.push(formatDuration(computedDuration));
    }
    // Pages for PDF
    else if (type === 'pdf' || type === 'notes' || (documentPages !== undefined && documentPages !== null)) {
      // If it's a PDF/notes, even if it's 0 currently from server, show at least 1 or the actual number.
      const effectivePages = (documentPages && documentPages !== '0') ? documentPages : 1;
      parts.push(`${effectivePages} pages`);
    }
    // Questions for Questionnaire
    else if (questions) {
      parts.push(`${questions} questions`);
    }

    return parts.join(' | ');
  };

  // Determine if we should show a thumbnail
  const resourceType = (type || '').toLowerCase();
  const isVideoFile = typeof title === 'string' && (title.toLowerCase().endsWith('.mp4') || title.toLowerCase().endsWith('.mov') || title.toLowerCase().endsWith('.webm') || title.toLowerCase().includes('youtube'));
  const isVideoType = resourceType === 'video' || resourceType === 'youtube' || resourceType === 'recording' || resourceType === 'ai-summary' || resourceType === 'ai-generated' || isVideoFile;
  const showThumbnail = !!(thumbnail || isVideoType);

  const isPdfType = type === 'pdf' || type === 'notes' || (title && title.toLowerCase().endsWith('.pdf'));

  // Locally uploaded videos go through the same server-side transcoding step
  // class recordings do, so they need the same "not playable until it finishes"
  // gating - previously this only applied to resourceType === 'recording',
  // which meant a freshly uploaded video's Play button was enabled immediately,
  // before the backend had actually finished processing it.
  const isPendingTranscode = ((resourceType === 'recording' || resourceType === 'video') && !transcoded) || String(props.status).toLowerCase() === 'draft';

  // A class recording only gets its own admin-library row (a real, numeric id) once
  // transcoding finishes and that row is backfilled — until then, or even after (it's
  // never guaranteed for broadcast/live-class recordings), it's identified by roomName
  // instead. ResourceAddForm.js already routes the actual save to the right endpoint
  // for either case (roomName-keyed rename vs admin-library patch) — Edit only needs
  // blocking here when NEITHER identifier is available, i.e. there's truly nothing to
  // save against.
  const isEditableRecording = resourceType !== 'recording' || !!roomName || !isNaN(Number(id));

  // Beyond YouTube, don't offer Download for any row with nothing behind it. This mirrors
  // the key resolution in ScheduleDetailsDrawer's handleDownloadResource field for field,
  // so the menu can never offer a download that handler will refuse with "no file path
  // found". Recordings are the exception: they download through the VOD server keyed by
  // roomName rather than an S3 path, so they qualify without one.
  const hasDownloadableFile = Boolean(
    filePath || file || props?.videoPath || props?.path || props?.s3Path ||
    props?.fileKey || props?.key || roomName || resourceType === 'recording'
  );

  // The "..." menu is left in antd's default container (document.body), which is the
  // only place it anchors to the trigger correctly: rendering it nearer the card is not an
  // option, because .resource-item sets overflow: hidden and clips it out of existence,
  // and rendering it into the scrolling .content misplaced it entirely.
  //
  // Body-rendered means it is positioned once, from coordinates measured as it opens, and
  // those stop being true the moment the list scrolls underneath it - which is how it
  // ended up floating away from its three dots. Close it on any scroll rather than let it
  // drift: the menu is either where it belongs or it is gone.
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    // Capture phase: the scroll happens on .content, and scroll events don't bubble.
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [menuOpen]);

  const handlePlayClick = () => {
    if (isPendingTranscode) return;
    if (isYoutubeLink) {
      window.open(link, '_blank');
    } else {
      onPlay?.(props);
    }
  };

  const handleViewClick = () => {
    // `file` first, not `filePath`: this is the field the Digital Library's own viewer
    // resolves (contentPage.js/subjectDetails.js pass item.file into LectureNotes, which
    // is what view-content ends up framing). A row can carry both and they are not always
    // the same object.
    const resolvedPath = file || filePath;
    const fileUrl = resolvedPath ? `${Api.dlS3Url}${resolvedPath}` : link;
    if (!fileUrl) return;
    // Open it in place (see ScheduleDetailsDrawer's pdfPreview) rather than in a browser
    // tab. A PDF in its own tab is rendered by the browser's viewer, whose toolbar has a
    // download button we can't reach - which would hand a teacher a copy of a resource
    // the ownership rule says they may not download.
    if (onView) {
      onView({ ...props, viewUrl: fileUrl });
      return;
    }
    window.open(fileUrl, '_blank');
  };

  const actionMenu = (
    // Close on any item, not just the ones that stay on this screen. The menu is
    // rendered into document.body at z-index 100000, so it outranks everything the
    // actions open on top of the list - View put the PDF preview up with View/Edit/
    // Download/Delete still floating over it. Menu's own onClick fires for every item
    // (after that item's handler), which is one place instead of six.
    <Menu className="resource-action-menu" onClick={() => setMenuOpen(false)}>
      {isVideoType && (
        <Menu.Item key="play" className="menu-item" disabled={isPendingTranscode} onClick={({ domEvent }) => { domEvent.stopPropagation(); handlePlayClick(); }}>Play</Menu.Item>
      )}
      {isPdfType && (
        <Menu.Item key="view" className="menu-item" onClick={({ domEvent }) => { domEvent.stopPropagation(); handleViewClick(); }}>View</Menu.Item>
      )}
      {showEdit && (
        <Menu.Item
          key="edit"
          className="menu-item"
          onClick={({ domEvent }) => {
            domEvent.stopPropagation();
            if (!isEditableRecording) {
              message.info('This recording is not available to edit right now.');
              return;
            }
            onEdit?.(props);
          }}
        >
          Edit
        </Menu.Item>
      )}
      {showDownload && !isYoutubeLink && hasDownloadableFile && (
        <Menu.Item key="download" className="menu-item" onClick={({ domEvent }) => { domEvent.stopPropagation(); onDownload?.(props); }}>Download</Menu.Item>
      )}
      {/* Publish/Unpublish only makes sense for class recordings — other resource
          types (pdf/video/ai-generated etc.) don't have a publish concept. */}
      {showPublish && resourceType === 'recording' && (
        <Menu.Item key="publish" className="menu-item" onClick={({ domEvent }) => {
          domEvent.stopPropagation();
          const newState = !isPublished;
          requestConfirm?.({
            title: 'Confirm',
            message: `Are you sure you want to ${newState ? 'publish' : 'unpublish'} the resource?`,
            onConfirm: () => {
              setIsPublished(newState);
              newState ? onPublish?.(id || title, type) : onUnpublish?.(id || title, type);
            }
          });
        }}>{isPublished ? "Unpublish" : "Publish"}</Menu.Item>
      )}
      <Menu.Item key="delete" className="menu-item delete" onClick={({ domEvent }) => {
        domEvent.stopPropagation();
        onDelete?.(props);
      }}>Delete</Menu.Item>
    </Menu>
  );

  return (
    <div className={`resource-item${showThumbnail ? ' resource-item--video' : ''}`}>
      <div className="resource-content">
        {showThumbnail ? (
          // Class recordings use the same thumbnail treatment as locally uploaded
          // videos (thumbnail if the resource has one, else the shared placeholder) —
          // isVideoType/showThumbnail above already covers resourceType === 'recording',
          // so no separate branch is needed here.
          <div className="thumbnail">
            <img
              src={thumbnail || videoThumbnailPlaceholder}
              alt={title}
              onError={(e) => { e.target.src = videoThumbnailPlaceholder; }}
            />
          </div>
        ) : (
          <div className="icon-wrapper">
            <img src={getIcon()} alt="type" width={24} height={24} />
          </div>
        )}
        <div className="resource-info" style={{ flex: 1, minWidth: 0 }}>
          {/* overlayStyle zIndex: the tooltip is portalled to document.body at antd's
              default z-index of 1070, while ScheduleDetailsDrawer renders at 99999 -
              so it was painting *behind* the drawer and never visible, which read as
              "no tooltip on long resource names". Same 100000 the action Dropdown
              below already needed, for the same reason. */}
          <Tooltip
            title={title}
            placement="topLeft"
            mouseEnterDelay={0.3}
            overlayStyle={{ zIndex: 100000, maxWidth: 420 }}
          >
            <h4
              className="resource-title"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                display: 'block'
              }}
              title=""
            >
              {title}
            </h4>
          </Tooltip>
          {isPendingTranscode ? (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
              <FaInfoCircle className="lowgrey" style={{ marginRight: 6, flexShrink: 0 }} />
              <span className="lowgrey text-xmd">{resourceType === 'recording' ? 'The recording video' : 'This video'} is being transcoded, kindly check later</span>
            </div>
          ) : (
            <p className="resource-meta">{getMetadata()}</p>
          )}
        </div>
      </div>

      {/* Recordings AND locally uploaded videos still being transcoded aren't
          playable/editable/deletable yet on the backend — hide the "..." menu
          entirely while pending, not just the Play item. */}
      {!isReadOnly && !isPendingTranscode && (
        <div className="resource-actions">
          <Dropdown
            overlay={actionMenu}
            trigger={['click']}
            placement="bottomRight"
            overlayClassName="resource-popover"
            overlayStyle={{ zIndex: 100000 }}
            visible={menuOpen}
            onVisibleChange={setMenuOpen}
          >
            <button
              className={`action-btn dropdown-trigger${menuOpen ? ' dropdown-trigger--open' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={threeDotsVertical} alt="actions" width={24} height={24} />
            </button>
          </Dropdown>
        </div>
      )}
    </div>
  );
};

const ResourcesPanel = ({
  classRecordings = [],
  aiGenerated = [],
  pdfs = [],
  videos = [],
  onDeleteAll,
  onAddResource,
  onDownload,
  onView,
  onPublish,
  onUnpublish,
  onDelete,
  onRename,
  onEdit,
  onPlay,
  showTabs = true,
  isReadOnly = false,
  loading = false
}) => {
  const [activeTab] = useState('Resources');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '', onConfirm: () => { } });

  const requestConfirm = (config) => {
    setModalConfig(config);
    setModalVisible(true);
  };

  const totalResourceCount = classRecordings.length + aiGenerated.length + pdfs.length + videos.length;
  const hasResources = totalResourceCount > 0;
  const tabs = ['Details', 'Resources', 'Quizzes', 'Attendance'];

  const itemProps = {
    isReadOnly,
    onEdit,
    onRename,
    onDownload,
    onView,
    onPublish,
    onUnpublish,
    onDelete,
    onPlay,
    requestConfirm
  };

  return (
    <div className="resources-panel">
      {/* ================= STYLES ================= */}
      <style>{`
        .resources-panel {
          font-family: 'Roboto', sans-serif;
          background: #fff;
          width: 100%;
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: calc(100vh - 160px);
          overflow: hidden;
          max-width: 100%;
        }

        .tabs {
          display: flex;
          border-bottom: 1px solid #e6e6e6;
          padding: 0 32px;
          flex-shrink: 0;
        }

        .tab {
          padding: 16px 20px;
          font-size: 14px;
          font-weight: 500;
          color: #636363;
        }

        .tab.active {
          color: #594099;
          position: relative;
        }

        .tab.active::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 2px;
          background: #594099;
        }

        .content {
          padding: 24px 40px;
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          overflow-x: hidden;
          justify-content: ${hasResources ? 'flex-start' : 'center'};
          max-width: 100%;
        }

        .section {
          margin-bottom: 32px;
        }

        .section-title {
          font-family: Roboto;
          font-weight: 400;
          font-size: 12px;
          line-height: 100%;
          letter-spacing: 0%;
          vertical-align: middle;
          color: #636363;
          margin-bottom: 16px;
        }

        .resource-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          border: 1px solid #E6E6E6;
          background: #FAFAFA;
          border-radius: 8px;
          margin-bottom: 16px;
          min-width: 0;
          overflow: hidden;
          max-width: 100%;
        }

        /* Video-type cards (thumbnail flush closer to the card edge) sit 12px in
           on the left instead of the 20px used by icon-type cards - the thumbnail
           image itself supplies the rest of that visual inset. */
        .resource-item.resource-item--video {
          padding: 12px 20px 12px 12px;
        }

        .resource-content {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
          min-width: 0;
          overflow: hidden;
        }

        .thumbnail {
          width: 80px;
          height: 60px;
          border-radius: 6px;
          overflow: hidden;
        }

        .thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .icon-wrapper {
          width: 24px;
          height: 24px;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .resource-title {
          font-size: 14px;
          font-weight: 500;
          color: #191919;
          margin: 0 0 8px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }

        .resource-meta {
          font-size: 12px;
          color: #636363;
        }

        .resource-actions {
          display: flex;
          gap: 12px;
        }

        .action-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          border-radius: 50%;
          transition: background 0.2s ease;
        }

        .action-btn:hover {
          background: #F0F0F0;
        }

        /* Held while the menu is open, so it stays obvious which card the floating menu
           belongs to - it is rendered into document.body and has no visual tie to its
           row otherwise. Deliberately a step darker than the hover state. */
        .dropdown-trigger--open,
        .dropdown-trigger--open:hover {
          background: #E0E0E0;
        }

        .btn-row {
          display: flex;
          gap: 28px;
          padding: 24px 40px;
          background: #fff;
          box-shadow: 0 -2px 8px rgba(0,0,0,0.06);
          flex-shrink: 0;
          z-index: 10;
        }

        .btn {
          flex: none;
          height: 45px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          letter-spacing: 0.5px;
          white-space: nowrap;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-delete {
          background: transparent;
          color: #FF414D;
          border: 1px solid #FF414D;
          width: 268px;
          padding: 13px 16px;
        }

        .btn-add {
          background: #594099;
          color: #FFFFFF;
          border: 1px solid #594099;
          width: 216px;
          padding: 13px 32px;
        }

        /* Dropdown Menu Styles */
        .resource-popover .ant-dropdown-menu {
          padding: 4px 0 !important;
          border-radius: 12px !important;
          box-shadow: 4px 4px 16px 0px rgba(0, 0, 0, 0.06) !important;
          /* Same edge-definition fix applied to the other dropdowns in this
             module - shadow alone is too soft against a white form background. */
          border: 1px solid #E6E6E6 !important;
        }

        .resource-action-menu {
          width: 112px;
        }

        .resource-action-menu.ant-menu .ant-menu-item {
          height: 40px;
          line-height: 40px;
          margin: 0 !important;
        }

        .resource-action-menu .menu-item {
          width: 100%;
          height: 40px;
          display: flex;
          align-items: center;
          padding: 0 12px;
          font-family: 'Roboto', sans-serif;
          font-weight: 400;
          font-size: 16px;
          color: #636363;
          cursor: pointer;
          transition: background 0.2s;
          margin: 0 !important;
        }

        .resource-action-menu .menu-item:hover {
          background: #C6C2FF;
        }

        .resource-action-menu .menu-item.delete,
        .resource-action-menu .menu-item.delete:hover {
          /* antd's own .ant-menu-item color rule was winning this one on
             specificity/order, so the red never actually rendered - force it. */
          color: #FF414D !important;
        }
      `}</style>

      {/* ================= TABS ================= */}
      {showTabs && (
        <div className="tabs">
          {tabs.map(tab => (
            <div
              key={tab}
              className={`tab ${activeTab === tab ? 'active' : ''}`}
            >
              {tab}
            </div>
          ))}
        </div>
      )}

      {/* ================= CONTENT + BUTTONS ================= */}
      <div className="content">
        {loading ? (
          // A resource was just added/edited and the list is being refetched — show a
          // loader instead of the stale pre-save list (or a false "no resources" empty
          // state) for that gap, rather than leaving the new resource looking like it
          // didn't get added until the refetch quietly resolves.
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
        {classRecordings.length > 0 && (
          <div className="section">
            <div className="section-title">Class recording</div>
            {classRecordings.map((item, i) => (
              <ResourceItem key={i} {...item} type="recording" showPublish={true} {...itemProps} />
            ))}
          </div>
        )}

        {aiGenerated.length > 0 && (
          <div className="section">
            <div className="section-title">AI generated</div>
            {aiGenerated.map((item, i) => (
              <ResourceItem key={i} {...item} type="ai-generated" showPublish={true} {...itemProps} />
            ))}
          </div>
        )}

        {(pdfs.length > 0 || videos.length > 0) && (
          <div className="section">
            <div className="section-title">Additional resources</div>
            {pdfs.map((item, i) => (
              <ResourceItem key={`pdf-${i}`} {...item} type="pdf" showPublish={true} {...itemProps} />
            ))}
            {videos.map((item, i) => (
              <ResourceItem key={`video-${i}`} {...item} type="video" showPublish={true} {...itemProps} />
            ))}
          </div>
        )}

        {!hasResources && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#636363' }}>
            <img
              src={emptyImage}
              alt="No resources"
              style={{ width: '220px', marginBottom: '24px' }}
            />
            <div style={{ fontSize: '14px', fontWeight: 500 }}>
              No resources found for this class.
            </div>
          </div>
        )}
          </>
        )}

      </div>

      {/* ================= ACTION BUTTONS (STICKY FOOTER) ================= */}
      {(() => {
        // Offered whenever the class actually has something to wipe. It does not delete
        // on the click itself - onDeleteAll opens the shared confirmation modal in
        // ScheduleDetailsDrawer - which is what makes exposing a bulk delete acceptable
        // here, given a class's recording can be swept up in it. Set this to false to
        // hide the button again.
        const showDeleteAll = totalResourceCount > 0;
        const allResources = [...classRecordings, ...aiGenerated, ...pdfs, ...videos];
        const nonTranscodingResources = allResources.filter(r => r.transcoded !== false && r.status !== 'Draft');
        const allAreTranscoding = allResources.length > 0 && nonTranscodingResources.length === 0;
        
        // A class can carry as many resources as it needs — recordings, uploaded videos
        // and PDFs alongside each other — so the button is always offered. It was
        // previously gated on the class having no video yet, back when a class was meant
        // to hold exactly one.
        const showAddResource = true;
        // Neither button applies (e.g. read-only, or the one video slot is already
        // filled and there's nothing else to bulk-delete) — don't render the footer
        // bar at all, it was showing up as an empty white box with nothing in it.
        if (isReadOnly || (!showDeleteAll && !showAddResource)) return null;
        return (
          <div className="btn-row" style={!hasResources ? { justifyContent: 'center' } : {}}>
            {showDeleteAll && (
              <button 
                className={`btn btn-delete ${allAreTranscoding ? 'disabled' : ''}`} 
                onClick={() => onDeleteAll(nonTranscodingResources)}
                disabled={allAreTranscoding}
                style={allAreTranscoding ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                DELETE ALL RESOURCES
              </button>
            )}
            {showAddResource && (
              <button
                className="btn btn-add"
                onClick={onAddResource}
                style={!hasResources ? { flex: 'none', width: '200px' } : {}}
              >
                ADD A RESOURCE
              </button>
            )}
          </div>
        );
      })()}

      <SavingModal
        visible={modalVisible}
        title={modalConfig.title}
        message={modalConfig.message}
        onConfirm={() => { modalConfig.onConfirm?.(); setModalVisible(false); }}
        onCancel={() => setModalVisible(false)}
      />
    </div>
  );
};

export default ResourcesPanel;
